/** @file TileCanvas: the zoomed tile with checkerboard, onion, pixel grid, pivot mark and pointer strokes. */
import type { Image } from '@zilo/pose3d-image-io/types/image';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { CSSProperties, PointerEvent, ReactElement } from 'react';
import { tileStroke } from './tileStroke';
import type { TileStrokeMemory } from './tileStroke';
import { tileZoomStep } from './tileZoomStep';

/**
 * The draft key and pixels, the pivot in tile pixels, the zoom, the onion tiles (drawn at 30 % under the pixels),
 * the grid toggle, a zoom request from the wheel, and a callback with the measured area (for zoom to fit).
 */
export type TileCanvasProps = {
  draftKey: string; image: Image; pivot: readonly [number, number]; zoom: number; onion: readonly Image[]; grid: boolean;
  onZoom: (zoom: number) => void; onArea: (area: [number, number]) => void;
};

const LAYER: CSSProperties = { position: 'absolute', inset: 0, width: '100%', height: '100%', imageRendering: 'pixelated', pointerEvents: 'none' };

/** A canvas showing `image` 1:1 (CSS scales it); nothing is drawn where the DOM has no 2D canvas (tests). */
function ImageLayer({ image, opacity, label }: { image: Image; opacity: number; label: string }): ReactElement {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (canvas === null || typeof ImageData === 'undefined') return;
    canvas.getContext('2d')?.putImageData(new ImageData(new Uint8ClampedArray(image.data), image.width, image.height), 0, 0);
  }, [image]);
  return <canvas ref={ref} aria-label={label} width={image.width} height={image.height} style={{ ...LAYER, opacity }} />;
}

/** The accent crosshair on the pivot point and a dashed box on the pivot pixel. */
function PivotMark({ pivot, zoom }: { pivot: readonly [number, number]; zoom: number }): ReactElement {
  const [px, py] = [pivot[0] * zoom, pivot[1] * zoom];
  const arm = Math.max(12, zoom * 1.5);
  const line = (style: CSSProperties): ReactElement => <div style={{ position: 'absolute', background: 'var(--p3-accent)', pointerEvents: 'none', ...style }} />;
  return (
    <div data-testid="pivot" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', left: Math.floor(pivot[0]) * zoom, top: Math.floor(pivot[1]) * zoom, width: zoom, height: zoom, outline: '1px dashed var(--p3-accent)', outlineOffset: -1 }} />
      {line({ left: px - arm, top: py, width: arm * 2, height: 1 })}
      {line({ left: px, top: py - arm, width: 1, height: arm * 2 })}
    </div>
  );
}

/** The scrolling area: the wheel zooms by whole steps around the pointer; presses paint through `tileStroke`. */
export function TileCanvas({ draftKey, image, pivot, zoom, onion, grid, onZoom, onArea }: TileCanvasProps): ReactElement {
  const area = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const anchor = useRef<{ fx: number; fy: number; cx: number; cy: number } | null>(null);
  const memory = useRef<TileStrokeMemory>({ last: null });
  const pivotX = pivot[0];
  const stroke = useMemo(() => tileStroke(draftKey, pivotX, memory.current), [draftKey, pivotX]);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  useEffect(() => {
    const box = area.current;
    if (box === null) return undefined;
    const measure = (): void => { onArea([box.clientWidth, box.clientHeight]); };
    measure();
    const wheel = (e: WheelEvent): void => {
      const r = stage.current?.getBoundingClientRect();
      if (r === undefined || e.deltaY === 0) return;
      e.preventDefault();
      const z = zoomRef.current;
      anchor.current = { fx: (e.clientX - r.left) / z, fy: (e.clientY - r.top) / z, cx: e.clientX, cy: e.clientY };
      onZoom(tileZoomStep(z, e.deltaY < 0 ? 1 : -1));
    };
    box.addEventListener('wheel', wheel, { passive: false });
    const watch = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    watch?.observe(box);
    return () => { box.removeEventListener('wheel', wheel); watch?.disconnect(); };
  }, [onZoom, onArea]);
  useLayoutEffect(() => {
    const a = anchor.current;
    const box = area.current;
    const r = stage.current?.getBoundingClientRect();
    anchor.current = null;
    if (a === null || box === null || r === undefined) return;
    box.scrollLeft += r.left + a.fx * zoom - a.cx;
    box.scrollTop += r.top + a.fy * zoom - a.cy;
  }, [zoom]);
  const pixelOf = (e: PointerEvent): [number, number] => {
    const r = e.currentTarget.getBoundingClientRect();
    return [Math.floor((e.clientX - r.left) / zoom), Math.floor((e.clientY - r.top) / zoom)];
  };
  const down = (e: PointerEvent<HTMLDivElement>): void => {
    if (e.button !== 0) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* no pointer capture here (jsdom) */ }
    const [x, y] = pixelOf(e);
    stroke.down(x, y, { alt: e.altKey, shift: e.shiftKey });
  };
  const gridStyle: CSSProperties = grid
    ? { backgroundImage: 'linear-gradient(to right, rgba(128,128,128,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(128,128,128,0.4) 1px, transparent 1px)', backgroundSize: `${String(zoom)}px ${String(zoom)}px` }
    : {};
  return (
    <div ref={area} className="p3-tile-area" data-testid="tile-area">
      <div ref={stage} className="p3-tile-stage" data-testid="tile-stage" data-zoom={zoom}
        style={{ width: image.width * zoom, height: image.height * zoom }}
        onPointerDown={down}
        onPointerMove={(e) => { if (e.buttons === 1) { const [x, y] = pixelOf(e); stroke.move(x, y); } }}
        onPointerUp={() => { stroke.up(); }}
        onPointerCancel={() => { stroke.up(); }}>
        {onion.map((img, i) => <ImageLayer key={String(i)} image={img} opacity={0.3} label="onion" />)}
        <ImageLayer image={image} opacity={1} label="tile pixels" />
        {grid ? <div data-testid="pixel-grid" style={{ ...LAYER, ...gridStyle }} /> : null}
        <PivotMark pivot={pivot} zoom={zoom} />
      </div>
    </div>
  );
}
